// =============================================================================
// CancelRefundModal — operator cancels a booking, optionally with a refund
// =============================================================================
// Shown from BookingDetailScreen when the operator clicks "Annuleren". Pulls
// the latest booking.payments row to determine the payment method, lets the
// operator pick refund=none/full/partial (only when payment_status='paid'),
// and POSTs to booking-cancel-refund.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatError } from '../lib/errors';
import {
  cancelBooking,
  type RefundKind,
} from '../api/cancel-refund';
import { cappedRefundableCents, scopedRefundableCents } from '../lib/refund-math';
import type { BookingRow } from '../types';

interface Props {
  booking: BookingRow;
  onClose: (changed: boolean) => void;
  /** Number of OTHER departures under the same parent booking. When > 0,
   *  cancelling this departure only reduces the booking; the siblings stay. */
  siblingCount?: number;
}

interface PaymentRow {
  amount_cents: number;
  payment_method: 'stripe' | 'cash' | 'card_terminal' | 'comp';
  stripe_payment_intent_id: string | null;
}

const METHOD_LABEL: Record<PaymentRow['payment_method'], string> = {
  stripe:        'Stripe (online)',
  cash:          'Contant',
  card_terminal: 'Pinterminal',
  comp:          'Comp / gratis',
};

const METHOD_REFUND_HINT: Record<PaymentRow['payment_method'], string> = {
  stripe:
    'Stripe maakt het bedrag terug naar de oorspronkelijke betaalmethode. ' +
    'Kan een paar werkdagen duren.',
  cash:
    'Het bedrag is door de operator contant teruggegeven aan de klant.',
  card_terminal:
    'De pinterminal-refund is door de operator uitgevoerd.',
  comp: '',
};

export function CancelRefundModal({ booking, onClose, siblingCount = 0 }: Props) {
  const [payment, setPayment] = useState<PaymentRow | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [refundKind, setRefundKind] = useState<RefundKind>('none');
  const [partialAmountInput, setPartialAmountInput] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  // 'departure' = cancel only this departure; 'booking' = cancel every
  // departure under the parent and issue one combined refund/voucher.
  const [scope, setScope] = useState<'departure' | 'booking'>('departure');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Set once a voucher refund succeeds — switches the modal to a success panel
  // that shows the issued code (copyable, in case the email didn't reach them).
  const [voucherResult, setVoucherResult] = useState<
    { code: string; amount_cents: number; email_sent: boolean } | null
  >(null);
  const [copied, setCopied] = useState(false);

  // ---- Load latest payments row -------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPaymentLoading(true);
      setPaymentError(null);
      try {
        // payments are keyed on the PARENT booking, not the departure.
        // booking.id here is the departure_id; the parent booking id lives
        // on parent_booking_id (same key booking-cancel-refund uses to find
        // the payment + issue the Stripe refund).
        const { data, error } = await supabase
          .schema('booking')
          .from('payments')
          .select('amount_cents, payment_method, stripe_payment_intent_id')
          .eq('booking_id', booking.parent_booking_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        setPayment((data as PaymentRow | null) ?? null);
      } catch (e) {
        if (!cancelled) setPaymentError(formatError(e));
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [booking.parent_booking_id]);

  // ---- Close on Escape ----------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose(voucherResult != null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting, voucherResult]);

  // ---- Default refund choice when payment loads ---------------------------
  useEffect(() => {
    if (!payment) {
      setRefundKind('none');
      return;
    }
    // Default: full refund when paid, otherwise none.
    if (booking.payment_status === 'paid') {
      setRefundKind('full');
    } else {
      setRefundKind('none');
    }
  }, [payment, booking.payment_status]);

  const isPaid = booking.payment_status === 'paid' && payment != null;
  const canPickRefund = isPaid;

  // Part of the booking paid with a physical gift card (gift_card_cents on the
  // picks). booking-cancel-refund puts it back on the card for Volledig and
  // Tegoedbon; the cash amounts above never include it (total = cash).
  const [giftCard, setGiftCard] = useState<{ departure: number; booking: number }>({ departure: 0, booking: 0 });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .schema('booking')
        .from('departure_experiences')
        .select('gift_card_cents, departure_id, departures!inner(booking_id)')
        .eq('departures.booking_id', booking.parent_booking_id);
      if (cancelled || !data) return;
      const rows = data as unknown as { gift_card_cents: number | null; departure_id: string }[];
      setGiftCard({
        departure: rows.filter((r) => r.departure_id === booking.id).reduce((n, r) => n + (r.gift_card_cents ?? 0), 0),
        booking: rows.reduce((n, r) => n + (r.gift_card_cents ?? 0), 0),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [booking.id, booking.parent_booking_id]);
  const giftCardCents = scope === 'booking' ? giftCard.booking : giftCard.departure;

  const partialAmountCents = useMemo(() => {
    const n = Number(partialAmountInput);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  }, [partialAmountInput]);

  // Refunds (full / partial / voucher) are based on THIS departure's current
  // value, not the original payment: after a party reduction (the removed seats
  // were already returned as a tegoedbon) or for one departure of a multi-
  // departure booking, the departure is worth less than the amount paid. Capped
  // at the amount actually paid (mirrors the edge function's refundableValue).
  const currentValueCents =
    booking.total_cents ?? (payment ? payment.amount_cents : 0);
  const refundableCents = cappedRefundableCents(
    currentValueCents,
    payment ? payment.amount_cents : null,
  );

  // Cancelling the WHOLE booking (multi-departure) refunds/vouchers the entire
  // payment, not just this departure's share.
  const wholeBookingCents = payment ? payment.amount_cents : 0;
  const effectiveRefundableCents = scopedRefundableCents(
    scope,
    refundableCents,
    wholeBookingCents,
  );

  const partialInvalid =
    refundKind === 'partial' &&
    (partialAmountCents == null || partialAmountCents > effectiveRefundableCents);

  const isArrived = booking.lifecycle_status === 'arrived';
  const isDraft = booking.lifecycle_status === 'draft';
  const cancellable =
    booking.lifecycle_status === 'confirmed' || isArrived || isDraft;

  const submitDisabled =
    submitting ||
    paymentLoading ||
    !cancellable ||
    partialInvalid;

  // ---- Submit -------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await cancelBooking({
        booking_id: booking.id,
        refund: refundKind,
        ...(refundKind === 'partial' && partialAmountCents != null
          ? { refund_amount_cents: partialAmountCents }
          : {}),
        reason: reason.trim().length > 0 ? reason.trim() : null,
        force_arrived: isArrived,
        scope,
      });
      if (!result.ok || !result.data) {
        const e = result.error;
        const msg = e?.message ?? e?.error ?? `HTTP ${result.status}`;
        setSubmitError(msg);
        return;
      }
      // Voucher refund: stay open and show the issued code instead of closing,
      // so the operator can copy it (and see whether the email went out).
      if (refundKind === 'voucher' && result.data.voucher) {
        setVoucherResult(result.data.voucher);
        return;
      }
      onClose(true);
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [
    submitDisabled, booking.id, refundKind, partialAmountCents, reason, onClose, isArrived, scope,
  ]);

  const copyVoucherCode = useCallback(async () => {
    if (!voucherResult) return;
    try {
      await navigator.clipboard.writeText(voucherResult.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API unavailable — operator can select the code manually */
    }
  }, [voucherResult]);

  // ---- Render -------------------------------------------------------------
  const paidEur = payment ? (payment.amount_cents / 100).toFixed(2) : null;
  // Amount shown on the refund radios/hints — reflects the chosen scope and is
  // already capped at what was paid.
  const departureEur = (effectiveRefundableCents / 100).toFixed(2);
  // Scope-selector labels (only shown for multi-departure bookings).
  const thisDepartureEur = (refundableCents / 100).toFixed(2);
  const wholeBookingEur = (wholeBookingCents / 100).toFixed(2);
  // Only a single-departure booking can be "partly returned" via a reduction.
  // For a multi-departure booking a smaller per-departure value is normal (not
  // a reduction), so the scope selector explains the amounts instead.
  const partlyReturned =
    siblingCount === 0 && payment != null && refundableCents < payment.amount_cents;

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* close via ✕ or Esc only — don't lose typed reason on a stray click */ }}>
      <div className="dep-modal cancel-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>Boeking annuleren</h2>
            <div className="dep-modal-sub muted">{booking.booking_number}</div>
          </div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => !submitting && onClose(voucherResult != null)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </header>

        <div className="dep-modal-body">
          {voucherResult ? (
            <div className="walkin-step">
              <div className="walkin-success-title">✓ Tegoedbon aangemaakt</div>
              <p className="muted">
                {voucherResult.email_sent
                  ? 'De tegoedbon is per e-mail naar de klant verstuurd.'
                  : 'Let op: de e-mail kon niet worden verstuurd. Geef de onderstaande code zelf aan de klant door.'}
              </p>
              <div className="cancel-voucher-result">
                <strong className="voucher-code">{voucherResult.code}</strong>
                <span className="muted">
                  {' · € '}{(voucherResult.amount_cents / 100).toFixed(2)}
                </span>
              </div>
              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  onClick={() => void copyVoucherCode()}
                >
                  {copied ? 'Gekopieerd ✓' : 'Kopieer code'}
                </button>
                <button
                  className="walkin-primary-btn"
                  onClick={() => onClose(true)}
                >
                  Sluiten
                </button>
              </div>
            </div>
          ) : paymentLoading ? (
            <div className="muted">Betalingsgegevens laden…</div>
          ) : paymentError ? (
            <div className="error-card">{paymentError}</div>
          ) : (
            <>
              <div className="cancel-summary">
                <div className="muted">
                  {booking.experiences?.name ?? '—'} ·{' '}
                  {booking.party_size}p · €{' '}
                  {booking.total_cents != null
                    ? (booking.total_cents / 100).toFixed(2)
                    : '—'}
                </div>
                <div className="muted">
                  {payment
                    ? <>Betaald via <strong>{METHOD_LABEL[payment.payment_method]}</strong> · € {paidEur}</>
                    : <>Geen betaling geregistreerd</>}
                </div>
                {partlyReturned ? (
                  <div className="muted cancel-partly-returned">
                    Deze boeking is verkleind — € {departureEur} is nog
                    terugbetaalbaar (het restant is al als tegoedbon uitgegeven).
                  </div>
                ) : null}
              </div>

              {siblingCount > 0 ? (
                <fieldset className="cancel-refund-options cancel-scope-options">
                  <legend>Wat annuleren?</legend>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={scope === 'departure'}
                      onChange={() => setScope('departure')}
                    />
                    <span>Alleen dit vertrek (€ {thisDepartureEur})</span>
                  </label>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={scope === 'booking'}
                      onChange={() => setScope('booking')}
                    />
                    <span>
                      Hele boeking — {siblingCount + 1} vertrekken (€ {wholeBookingEur})
                    </span>
                  </label>
                </fieldset>
              ) : null}

              {canPickRefund ? (
                <fieldset className="cancel-refund-options">
                  <legend>Terugbetalen</legend>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={refundKind === 'full'}
                      onChange={() => setRefundKind('full')}
                    />
                    <span>
                      Volledig (€ {departureEur})
                    </span>
                  </label>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={refundKind === 'partial'}
                      onChange={() => setRefundKind('partial')}
                    />
                    <span>Gedeeltelijk</span>
                    {refundKind === 'partial' ? (
                      <input
                        type="number"
                        min={0.01}
                        max={effectiveRefundableCents / 100}
                        step="0.01"
                        placeholder="bedrag €"
                        className="cancel-partial-input"
                        value={partialAmountInput}
                        onChange={(e) => setPartialAmountInput(e.target.value)}
                      />
                    ) : null}
                  </label>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={refundKind === 'voucher'}
                      onChange={() => setRefundKind('voucher')}
                    />
                    <span>Tegoedbon (€ {departureEur})</span>
                  </label>
                  <label className="cancel-radio">
                    <input
                      type="radio"
                      checked={refundKind === 'none'}
                      onChange={() => setRefundKind('none')}
                    />
                    <span>Niet terugbetalen</span>
                  </label>

                  {refundKind === 'voucher' ? (
                    <p className="muted cancel-method-hint">
                      Er wordt een tegoedbon van € {departureEur} per e-mail naar
                      de klant gestuurd. De betaling wordt niet teruggestort.
                    </p>
                  ) : (refundKind === 'full' || refundKind === 'partial') &&
                    payment ? (
                    <p className="muted cancel-method-hint">
                      {METHOD_REFUND_HINT[payment.payment_method]}
                    </p>
                  ) : null}

                  {giftCardCents > 0 ? (
                    <p className="muted cancel-method-hint">
                      € {(giftCardCents / 100).toFixed(2)} is met een cadeaubon betaald.
                      {refundKind === 'full' || refundKind === 'voucher'
                        ? ' Dat bedrag gaat terug op dezelfde cadeaubon.'
                        : ' Bij Volledig of Tegoedbon gaat dat terug op de cadeaubon; hier niet.'}
                    </p>
                  ) : null}

                  {partialInvalid ? (
                    <p className="cancel-warn">
                      Bedrag moet groter zijn dan 0 en niet meer dan het
                      betaalde totaal.
                    </p>
                  ) : null}
                </fieldset>
              ) : (
                <p className="muted">
                  Geen actieve betaling om terug te betalen. De boeking wordt
                  alleen geannuleerd.
                </p>
              )}

              <label className="walkin-field cancel-reason">
                <span>Reden (optioneel, wordt gelogd)</span>
                <textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Bijv. klant zelf gebeld, ziekte, …"
                />
              </label>

              {submitError ? (
                <div className="error-card walkin-submit-error">{submitError}</div>
              ) : null}

              {isArrived ? (
                <div className="cancel-warn">
                  <strong>Let op:</strong> deze klant is al ingecheckt. Annuleren
                  zet de boeking en alle slots op <strong>cancelled</strong>.
                  De check-in blijft zichtbaar in de geschiedenis.
                </div>
              ) : !cancellable ? (
                <div className="cancel-warn">
                  Deze boeking is{' '}
                  <strong>{booking.lifecycle_status}</strong> en kan niet meer
                  worden geannuleerd.
                </div>
              ) : null}

              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  disabled={submitting}
                  onClick={() => onClose(false)}
                >
                  Sluiten
                </button>
                <button
                  className="walkin-primary-btn cancel-confirm-btn"
                  disabled={submitDisabled}
                  onClick={() => void handleSubmit()}
                >
                  {submitting
                    ? 'Bezig…'
                    : refundKind === 'none'
                      ? 'Boeking annuleren'
                      : refundKind === 'voucher'
                        ? 'Annuleren + tegoedbon'
                        : 'Annuleren + terugbetalen'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
